import { AlertCircle, Upload } from "lucide-react";
import { useRef, useState } from "react";

import { ARTIFACT_PAGE_LIMIT, type Artifact } from "@/api/artifacts";
import { MOLECULE_ACCEPT_ATTRIBUTE } from "@/api/molecules";
import { ArtifactStatusBadge } from "@/components/artifacts/ArtifactStatusBadge";
import { MoleculePicker } from "@/components/molecules/MoleculePicker";
import type { MoleculeOption } from "@/components/molecules/molecule-option";
import { Select } from "@/components/ui/select";
import { shortHash } from "@/lib/hash-display";
import { cn } from "@/lib/utils";

/**
 * Typed-object port types the picker can seed from a local file upload.
 * Only `Molecule` has a server-side ingest (POST /api/molecules →
 * seed_molecule_from_sdf); a `ProteinStructure` from_pdb analogue lands
 * with protein-prep Tiles (P1).
 */
const UPLOADABLE_TYPES = new Set<string>(["Molecule"]);

/**
 * Manifest-declared input port shape on the wire.
 *
 * Mirrors `src/flowstack/manifest/schema.py:InputPort`. Slice 2's
 * submission API supports `cardinality="one"` only; the picker
 * surfaces `cardinality="many"` ports as disabled with a deferred
 * note so users see why they can't select via this surface.
 */
export interface InputPort {
  type: string;
  cardinality?: "one" | "many";
  required?: boolean;
  description?: string;
}

interface ArtifactInputPickerProps {
  /**
   * Map of port_name → InputPort from `manifest.inputs`. Pass
   * undefined when the manifest hasn't loaded yet; the picker
   * renders a Skeleton.
   */
  inputs?: Record<string, InputPort>;
  /**
   * All artifacts the catalog returned (`useArtifacts().data`).
   * The picker filters per-port by `typed_object === port.type`.
   */
  artifacts: readonly Artifact[];
  /** Currently-selected `content_hash` per port. */
  selected: Record<string, string>;
  /**
   * Called when the user changes a port's selection. Pass `undefined`
   * for `contentHash` to clear the selection (e.g. user picked the
   * "—" empty option).
   */
  onChange: (portName: string, contentHash: string | undefined) => void;
  /**
   * Seed a Molecule from a local structure file (dispatch 039). When
   * provided, an upload control appears under each `Molecule` port; on success
   * the returned artifact is auto-selected for that port. Omit it (e.g.
   * component tests, or a surface without upload) and the control is
   * hidden — the picker stays pick-only. Rejects with an `ApiError`
   * whose `.message` the control surfaces inline.
   */
  onUploadMolecule?: (file: File, declaredCharge: number) => Promise<Artifact>;
  isLoading?: boolean;
}

/**
 * M9 #60 slice 3: artifact picker for declared input ports.
 *
 * Sits above the parameter form in the submit page. For each
 * `cardinality="one"` input port the manifest declares, the picker
 * renders a `ui/select` listing artifacts filtered by
 * `typed_object === port.type`. Each option shows the artifact's
 * short content_hash + producer Tile + a `scientific_status` icon
 * so the user sees the safety status at selection time.
 *
 * A `Molecule` port gets {@link MoleculePicker} instead (flowstack#1402):
 * options read formula · atoms · charge, origin and date, then the digest,
 * with a filter box over them — a digest alone identified nothing.
 *
 * `cardinality="many"` (fan-in) ports are rendered disabled with a
 * "dispatched via the Flow runner" note; the backend rejects them
 * with `many_cardinality_not_yet_supported` (422) so this matches
 * the contract.
 *
 * The picker doesn't enforce the failed-upstream block here — that
 * lives in {@link FailedUpstreamBanner} so the warning + override
 * checkbox can sit near the Submit button.
 */
export function ArtifactInputPicker({
  inputs,
  artifacts,
  selected,
  onChange,
  onUploadMolecule,
  isLoading,
}: ArtifactInputPickerProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-10 animate-pulse rounded-md bg-muted/40" />
        <div className="h-10 animate-pulse rounded-md bg-muted/40" />
      </div>
    );
  }

  const portEntries = inputs ? Object.entries(inputs) : [];
  if (portEntries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This Tile declares no input artifacts; all parameters are user-supplied.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {portEntries.map(([portName, port]) => (
        <PortRow
          key={portName}
          portName={portName}
          port={port}
          artifacts={artifacts}
          selectedHash={selected[portName]}
          onChange={onChange}
          onUploadMolecule={onUploadMolecule}
        />
      ))}
    </div>
  );
}

interface PortRowProps {
  portName: string;
  port: InputPort;
  artifacts: readonly Artifact[];
  selectedHash: string | undefined;
  onChange: (portName: string, contentHash: string | undefined) => void;
  onUploadMolecule?: (file: File, declaredCharge: number) => Promise<Artifact>;
}

function PortRow({
  portName,
  port,
  artifacts,
  selectedHash,
  onChange,
  onUploadMolecule,
}: PortRowProps): JSX.Element {
  const cardinality = port.cardinality ?? "one";
  const isMany = cardinality === "many";
  // Filter by typed_object then dedupe by content_hash. The catalog
  // is content-addressed (anchor §5.2) so two rows with the same
  // payload share a hash; the option value IS the content_hash, so
  // multiple options with the same value would be indistinguishable
  // in the select. Keep the first encountered row (the list is
  // already sorted by created_at desc on the server) — per Rupert
  // PR-300 r1.
  const matching = (() => {
    const seen = new Set<string>();
    const out: Artifact[] = [];
    for (const a of artifacts) {
      if (a.typed_object !== port.type) continue;
      if (seen.has(a.content_hash)) continue;
      seen.add(a.content_hash);
      out.push(a);
    }
    return out;
  })();
  const selectId = `artifact-input-${portName}`;
  const isMoleculePort = port.type === "Molecule" && !isMany;
  const selectedArtifact = selectedHash
    ? matching.find((a) => a.content_hash === selectedHash)
    : undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={selectId} className="flex items-baseline gap-2 text-sm font-medium">
        <span>{portName}</span>
        <span className="font-mono text-xs text-muted-foreground">{port.type}</span>
        {port.required !== false && (
          <span
            className="rounded bg-state-failed-validation-fg/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-state-failed-validation-fg"
            aria-label="required"
          >
            required
          </span>
        )}
        {isMany && (
          <span
            className="label-micro rounded bg-muted px-1.5 py-0.5 text-muted-foreground"
            aria-label="cardinality: many"
            title="Fan-in ports are dispatched via the Flow runner (P1.2); not supported on the per-Tile submit surface."
          >
            many (Flow runner)
          </span>
        )}
      </label>
      {port.description && (
        <p id={`${selectId}-desc`} className="text-xs text-muted-foreground">
          {port.description}
        </p>
      )}
      {isMoleculePort ? (
        // flowstack#1402: a Molecule is picked by what it is, with a filter.
        <MoleculePicker
          id={selectId}
          options={matching.map(toMoleculeOption)}
          value={selectedHash}
          onChange={(next) => onChange(portName, next)}
          placeholder={`— select a ${port.type} —`}
          // Not the port name: the port's `<label>` names the select, and a
          // second control answering to it would make that name ambiguous.
          filterLabel="Filter molecules"
          aria-describedby={port.description ? `${selectId}-desc` : undefined}
        />
      ) : (
        <Select
          id={selectId}
          value={selectedHash ?? ""}
          disabled={isMany}
          onChange={(e) => {
            const next = e.target.value;
            onChange(portName, next === "" ? undefined : next);
          }}
          className="w-full"
          aria-describedby={port.description ? `${selectId}-desc` : undefined}
        >
          <option value="">— select a {port.type} —</option>
          {matching.map((artifact) => (
            // Per Rupert PR-300 r0: key on artifact_id (unique per
            // catalog row) rather than content_hash. The catalog is
            // content-addressed so two rows can legitimately share a
            // content_hash (same payload produced by different runs);
            // keying on content_hash would emit React duplicate-key
            // warnings and let the dedup-by-hash story silently break.
            <option key={artifact.artifact_id} value={artifact.content_hash}>
              {formatOption(artifact)}
            </option>
          ))}
        </Select>
      )}
      {/* The Molecule picker summarises its own selection; what it cannot
          know about is a selection outside the rows it was given. */}
      {selectedHash && !(isMoleculePort && selectedArtifact) && (
        <SelectedArtifactSummary artifact={selectedArtifact} />
      )}
      {!isMany &&
        matching.length === 0 &&
        !(onUploadMolecule && UPLOADABLE_TYPES.has(port.type)) && (
          <p className="text-xs text-muted-foreground">
            No {port.type} artifacts in the catalog yet. Produce one upstream first.
          </p>
        )}
      {!isMany && onUploadMolecule && UPLOADABLE_TYPES.has(port.type) && (
        <MoleculeUploadControl
          portName={portName}
          hasExisting={matching.length > 0}
          onUpload={onUploadMolecule}
          onSeeded={(artifact) => onChange(portName, artifact.content_hash)}
        />
      )}
    </div>
  );
}

interface MoleculeUploadControlProps {
  portName: string;
  hasExisting: boolean;
  onUpload: (file: File, declaredCharge: number) => Promise<Artifact>;
  onSeeded: (artifact: Artifact) => void;
}

/**
 * Inline "Upload molecule" control for a Molecule port. Seeds the catalog
 * server-side (POST /api/molecules) and auto-selects the result. Kept
 * local (its own file/charge/pending/error state) so the picker above
 * stays a pure presentational component.
 *
 * The accepted extensions come from `MOLECULE_ACCEPT_ATTRIBUTE` rather than
 * a literal here, so this control and the client-side guard in
 * `api/molecules.ts` cannot disagree about what the server will take
 * (flowstack#1319).
 */
function MoleculeUploadControl({
  portName,
  hasExisting,
  onUpload,
  onSeeded,
}: MoleculeUploadControlProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [declaredCharge, setDeclaredCharge] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileInputId = `molecule-upload-file-${portName}`;
  const chargeInputId = `molecule-upload-charge-${portName}`;

  async function handleUpload(): Promise<void> {
    if (!file) return;
    setPending(true);
    setError(null);
    try {
      const artifact = await onUpload(file, declaredCharge);
      onSeeded(artifact);
      // Reset the picker so a subsequent upload starts clean; the
      // seeded artifact is now the port's selection.
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setError(err instanceof Error ? err.message : "upload failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border-strong bg-muted/20 p-2.5">
      <p className="text-xs font-medium text-muted-foreground">
        {hasExisting
          ? `…or upload a new molecule (${MOLECULE_ACCEPT_ATTRIBUTE})`
          : `Upload a molecule to seed one (${MOLECULE_ACCEPT_ATTRIBUTE})`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          id={fileInputId}
          type="file"
          accept={MOLECULE_ACCEPT_ATTRIBUTE}
          aria-label={`Upload molecule for ${portName}`}
          disabled={pending}
          onChange={(e) => {
            setError(null);
            setFile(e.target.files?.[0] ?? null);
          }}
          className={cn(
            "max-w-full rounded-md text-xs",
            // The button inside the input. It is `::file-selector-button`, so
            // every rule reaching it needs the `file:` modifier — which is
            // exactly why it drifted out of the design system and shipped
            // painted with the PAGE's token while standing on a card
            // (flowstack#1403). Same recipe as `Button variant="outline"`
            // now: the card's own fill, and `--input` carrying the 3:1 edge.
            // `file:border-solid` is not redundant, and leaving it out is how
            // this stays broken after the colour is right: Tailwind's
            // preflight resets `border-style` on `*, ::before, ::after` and
            // NOT on `::file-selector-button`, so `file:border` sets a width
            // against the UA's inherited `outset`. Chromium then paints a
            // bevel — lightening two edges out of the colour we just measured
            // to 3.4:1 — and `tests/e2e/control_contrast.spec.ts` asserts the
            // computed style is `solid` for exactly that reason.
            "file:mr-2 file:rounded file:border file:border-solid file:border-input file:bg-card file:px-2 file:py-1 file:text-xs file:font-medium",
            // The sibling states the original had none of. `file:` and the
            // state modifier compose in this order — `hover:file:` styles the
            // button while the INPUT is hovered, which is the only hover a
            // native file button can be given. `enabled:` in front of it
            // because `disabled` suppresses pointer *events*, not `:hover`:
            // without it the button still lit up under the cursor for the
            // whole upload, contradicting the `pending` state beside it.
            "file:cursor-pointer file:transition-colors enabled:hover:file:bg-muted",
            // Focus lands on the input, never on the pseudo-element, so the
            // ring goes here and the whole control shows it.
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:file:cursor-not-allowed"
          )}
        />
        <label
          htmlFor={chargeInputId}
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          charge
          <input
            id={chargeInputId}
            type="number"
            step={1}
            value={declaredCharge}
            disabled={pending}
            aria-label={`Formal charge for ${portName} upload`}
            onChange={(e) => setDeclaredCharge(Number.parseInt(e.target.value, 10) || 0)}
            className="w-16 rounded-md border border-input bg-card px-2 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </label>
        <button
          type="button"
          disabled={!file || pending}
          onClick={handleUpload}
          // `enabled:hover:` — this button is disabled until a file is chosen,
          // which is most of its life, and a plain `hover:bg-muted` filled it
          // in under the cursor of the user trying to work out why it will not
          // click. `disabled:cursor-not-allowed` is what answers them, and it
          // rules out the `disabled:pointer-events-none` that `Button` uses.
          className="inline-flex items-center gap-1 rounded-md border border-input bg-card px-2.5 py-1 text-xs font-medium transition-colors enabled:hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Upload className="h-3 w-3" aria-hidden />
          {pending ? "Uploading…" : "Upload molecule"}
        </button>
      </div>
      {error && (
        <p role="alert" className="flex items-center gap-1 text-xs text-state-failed-validation-fg">
          <AlertCircle className="h-3 w-3" aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}

/** Map a catalog row onto the props-only picker's shape (flowstack#1402). */
function toMoleculeOption(artifact: Artifact): MoleculeOption {
  const molecule = artifact.molecule ?? null;
  return {
    id: artifact.content_hash,
    digest: artifact.content_hash,
    formula: molecule?.formula ?? null,
    atomCount: molecule?.atom_count ?? null,
    formalCharge: molecule?.formal_charge ?? null,
    smiles: molecule?.smiles ?? null,
    inchiKey: molecule?.inchi_key ?? null,
    createdAt: artifact.created_at,
    producer: artifact.producer_tile,
    runId: artifact.run_id,
  };
}

function formatOption(artifact: Artifact): string {
  const producer = artifact.producer_tile ?? "uploaded";
  const status = artifact.scientific_status;
  const statusTag = status ? ` [${status}]` : "";
  // The app's ONE identifier elision (`lib/hash-display`) — an `<option>` has
  // no `title` to fall back on, so the elided form has to be the discriminating
  // one, which a leading slice is not: two artifacts of the same producer can
  // share a 12-character prefix.
  return `${shortHash(artifact.content_hash)} — ${producer}${statusTag}`;
}

function SelectedArtifactSummary({ artifact }: { artifact?: Artifact }): JSX.Element {
  if (!artifact) {
    return (
      <p className="text-xs text-muted-foreground">
        Selected artifact is not among the {ARTIFACT_PAGE_LIMIT} most recent artifacts this picker
        loads. Find it on the Artifacts page, which searches the whole catalog.
      </p>
    );
  }
  const status = artifact.scientific_status;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <ArtifactStatusBadge status={status} />
      <span className="font-mono" title={artifact.content_hash}>
        {shortHash(artifact.content_hash)}
      </span>
      {artifact.producer_tile && (
        <span>
          from <span className="font-mono">{artifact.producer_tile}</span>
        </span>
      )}
    </div>
  );
}
